import { useEffect, useId, useRef, useState, type KeyboardEvent } from 'react';
import { useNavigate } from 'react-router';
import type { SearchResult } from './types';

export const searchPlaceholder = '設定を検索（例: 最大人数、メモリ、ホワイトリスト）';

export function SearchBox({
  results,
  query,
  onQuery,
  onSelect,
  onEscape,
  alwaysOpen = false,
}: {
  results: SearchResult[];
  query: string;
  onQuery: (query: string) => void;
  onSelect?: () => void;
  onEscape?: () => void;
  alwaysOpen?: boolean;
}) {
  const navigate = useNavigate();
  const id = useId();
  const input = useRef<HTMLInputElement>(null);
  const [active, setActive] = useState(0);
  const [open, setOpen] = useState(alwaysOpen);
  const visible = open && (alwaysOpen || Boolean(query.trim()));
  const selected = Math.min(active, Math.max(0, results.length - 1));
  useEffect(() => {
    if (visible) document.getElementById(`${id}-${selected}`)?.scrollIntoView?.({ block: 'nearest' });
  }, [id, selected, visible]);
  const choose = (result: SearchResult) => {
    navigate(result.url);
    setOpen(false);
    onQuery('');
    onSelect?.();
  };
  const onKey = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.nativeEvent.isComposing) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      setOpen(false);
      onEscape?.();
    } else if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      setOpen(true);
      setActive((selected + (event.key === 'ArrowDown' ? 1 : -1) + results.length) % (results.length || 1));
    } else if (event.key === 'Enter' && visible && results[selected]) {
      event.preventDefault();
      choose(results[selected]);
    }
  };
  return (
    <div
      className={`settings-search${alwaysOpen ? ' search-palette' : ''}`}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) setOpen(false);
      }}
    >
      <input
        ref={input}
        className="input"
        role="combobox"
        aria-label={searchPlaceholder}
        placeholder={searchPlaceholder}
        autoComplete="off"
        value={query}
        aria-expanded={visible}
        aria-controls={`${id}-list`}
        aria-autocomplete="list"
        aria-activedescendant={visible && results[selected] ? `${id}-${selected}` : undefined}
        onFocus={() => setOpen(true)}
        onKeyDown={onKey}
        onChange={(event) => {
          onQuery(event.target.value);
          setActive(0);
          setOpen(true);
        }}
      />
      {visible && (
        <div className="search-results">
          <div id={`${id}-list`} role="listbox" aria-label="検索候補">
            {results.map((result, index) => (
              <div key={result.id}>
                {(index === 0 || results[index - 1]?.group !== result.group) && (
                  <div className="search-group">{result.group}</div>
                )}
                <div
                  id={`${id}-${index}`}
                  role="option"
                  aria-selected={index === selected}
                  className={`search-option${index === selected ? ' active' : ''}`}
                  onMouseDown={(event) => event.preventDefault()}
                  onMouseMove={() => setActive(index)}
                  onClick={() => choose(result)}
                >
                  <strong>{result.name}</strong>
                  <span className="small muted">{result.description}</span>
                </div>
              </div>
            ))}
          </div>
          {!results.length && (
            <p role="status">見つかりませんでした。「人数」「RAM」「許可リスト」など、別の言葉でお試しください。</p>
          )}
          <div className="small muted search-help">↑↓ で選択・Enter で移動・Esc で閉じる</div>
        </div>
      )}
    </div>
  );
}
